import { HttpRequest, HttpMethod, axiosInstance } from "./http";

// Customer affiliate — BO program pages on the App PG (Phase 3 spec
// 2026-10-13 §3.7). Envelope {data: …} (+ total on lists). Errors carry a
// stable `code`: schema_missing (503), invalid_body (+ errors[{field, rule}]),
// stale, not_found, target_not_found, not_submitted, not_cancellable,
// affiliate_not_found, no_file, invalid_range. Actions are POST /:id/<verb>.
// Roles: marketing (program) / affiliate_finance (documents, adjustments).
const BASE = "/api/v1/bo/affiliate";

class AffiliateProvider extends HttpRequest {
    // Settings (+ env_enabled, updated_by_name)
    settings() {
        return this.request(HttpMethod.Get, `${BASE}/settings`);
    }
    // Settings fields + bump_terms_version + expected_updated_at (null when no row yet)
    saveSettings(payload) {
        return this.request(HttpMethod.Put, `${BASE}/settings`, payload);
    }

    // ?scope=&active=&q=
    rules(params) {
        return this.request(HttpMethod.Get, `${BASE}/rules`, params);
    }
    createRule(payload) {
        return this.request(HttpMethod.Post, `${BASE}/rules`, payload);
    }
    // RuleInput + expected_updated_at
    updateRule(id, payload) {
        return this.request(HttpMethod.Put, `${BASE}/rules/${id}`, payload);
    }
    disableRule(id) {
        return this.request(HttpMethod.Post, `${BASE}/rules/${id}/disable`, {});
    }
    enableRule(id) {
        return this.request(HttpMethod.Post, `${BASE}/rules/${id}/enable`, {});
    }
    // {data:{rule, chain}}
    effectiveRule(targetType, targetId) {
        return this.request(HttpMethod.Get, `${BASE}/rules/effective`, { target_type: targetType, target_id: targetId });
    }

    // ?q=&status=all|active|suspended|deleted&profile_status=&hold=&page=&limit≤100
    affiliates(params) {
        return this.request(HttpMethod.Get, `${BASE}/affiliates`, params);
    }
    exportAffiliatesCsv(params) {
        return axiosInstance.get(`${BASE}/affiliates/export.csv`, { params, responseType: "blob" });
    }
    // AffiliateRow + {clicks_total, orders_total, audit}
    affiliate(memberId) {
        return this.request(HttpMethod.Get, `${BASE}/affiliates/${memberId}`);
    }
    // {reason, hold_payout}
    suspend(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/affiliates/${memberId}/suspend`, payload);
    }
    unsuspend(memberId) {
        return this.request(HttpMethod.Post, `${BASE}/affiliates/${memberId}/unsuspend`, {});
    }
    // {hold, reason} — affiliate_finance
    hold(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/affiliates/${memberId}/hold`, payload);
    }

    // FinanceProfile (unmasked) — affiliate_finance
    profile(memberId) {
        return this.request(HttpMethod.Get, `${BASE}/affiliates/${memberId}/profile`);
    }
    // {data:{url, expires_in:300}} — each call is audited (file_view)
    fileUrl(memberId, type) {
        return this.request(HttpMethod.Get, `${BASE}/affiliates/${memberId}/profile/files/${type}/url`);
    }
    // {expected_updated_at}
    verify(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/affiliates/${memberId}/profile/verify`, payload);
    }
    // {reason, expected_updated_at}
    reject(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/affiliates/${memberId}/profile/reject`, payload);
    }

    // Per-member eligibility (owner 2026-10-16): only members granted here see
    // the Affiliate menu / may use the program. Errors: schema_missing (503,
    // sql/postgres/2026-10-16-affiliate-eligibility.sql not run yet),
    // member_not_found (404), invalid_body (note > 300).
    // ?q=&eligible=true|false|all&page=&limit≤100 → {data:[Eligibility], total}
    eligibilityList(params) {
        return this.request(HttpMethod.Get, `${BASE}/eligibility`, params);
    }
    // Eligibility + {program_enabled, env_enabled, audit:[last 20]}
    eligibility(memberId) {
        return this.request(HttpMethod.Get, `${BASE}/eligibility/${memberId}`);
    }
    // {note?} — marketing write; idempotent
    grantEligibility(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/eligibility/${memberId}/grant`, payload || {});
    }
    // {note?} — marketing write; never deletes links / commissions
    revokeEligibility(memberId, payload) {
        return this.request(HttpMethod.Post, `${BASE}/eligibility/${memberId}/revoke`, payload || {});
    }

    // ?from=&to=&status=&zero_reason=&kind=&affiliate_member_id=&code=&order_no=&payout_id=&page=&limit≤100
    commissions(params) {
        return this.request(HttpMethod.Get, `${BASE}/commissions`, params);
    }
    exportCommissionsCsv(params) {
        return axiosInstance.get(`${BASE}/commissions/export.csv`, { params, responseType: "blob" });
    }
    // {reason}
    cancelCommission(id, payload) {
        return this.request(HttpMethod.Post, `${BASE}/commissions/${id}/cancel`, payload);
    }
    // {affiliate_member_id, amount, order_no?, note} — affiliate_finance
    adjust(payload) {
        return this.request(HttpMethod.Post, `${BASE}/adjustments`, payload);
    }

    // ?from=&to=&group=day|month
    reportSummary(params) {
        return this.request(HttpMethod.Get, `${BASE}/reports/summary`, params);
    }
    // ?from=&to=&limit≤100
    reportTopAffiliates(params) {
        return this.request(HttpMethod.Get, `${BASE}/reports/top-affiliates`, params);
    }
    reportTopTargets(params) {
        return this.request(HttpMethod.Get, `${BASE}/reports/top-targets`, params);
    }
    // ?from=&to=&report=top-affiliates|top-targets|series
    exportReportCsv(params) {
        return axiosInstance.get(`${BASE}/reports/export.csv`, { params, responseType: "blob" });
    }
}

export default AffiliateProvider;
